import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../utils/api'

// ── Types ──────────────────────────────────────────────────────────────────────

interface AdminStats {
  totalUsers: number
  newUsersThisWeek: number
  totalSites: number
  publishedSites: number
  totalFormSubmissions: number
  totalOrders: number
  estimatedMrr: number
  activeSubscriptions: number
  planBreakdown: Array<{ plan: string; count: number }>
}

interface AdminUser {
  id: string
  email: string
  name: string | null
  plan: string
  isAdmin: boolean
  createdAt: string
  sitesCount: number
  hasActiveSubscription: boolean
  planExpiresAt: string | null
}

interface AdminSite {
  id: string
  slug: string
  name: string
  status: string
  suspended: boolean
  suspensionReason: string | null
  customDomain: string | null
  createdAt: string
  ownerEmail: string
  ownerPlan: string
  pagesCount: number
  submissionsCount: number
}

interface WaitlistEntry {
  id: string
  email: string
  createdAt: string
  position: number
}

interface AppSetting {
  key: string
  value: string
  updatedAt: string
}

interface Paginated<T> {
  data: T[]
  total: number
  page: number
  totalPages: number
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PLAN_COLORS: Record<string, string> = {
  FREE: '#9ca3af',
  HOSTING: '#0ea5e9',
  PERSONAL: '#10b981',
  BUSINESS: '#5b6af0',
  AGENCY: '#f59e0b',
  ENTERPRISE: '#7c3aed',
}

const PLAN_OPTIONS = ['FREE', 'HOSTING', 'PERSONAL', 'BUSINESS', 'AGENCY', 'ENTERPRISE']

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '8px', fontSize: '11px',
      fontWeight: 700, color: '#fff', background: PLAN_COLORS[plan] ?? '#666',
    }}>{plan}</span>
  )
}

function Pagination({ page, totalPages, total, unit, onPage }: {
  page: number; totalPages: number; total: number; unit: string
  onPage: (p: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
      <span style={{ fontSize: '13px', color: '#888' }}>Всего: {fmt(total)} {unit}</span>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button disabled={page === 1} onClick={() => onPage(page - 1)}
          style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: page === 1 ? '#f3f4f6' : '#fff', fontSize: '13px', color: page === 1 ? '#bbb' : '#333' }}>←</button>
        <span style={{ padding: '6px 12px', fontSize: '13px', color: '#555' }}>{page} / {totalPages}</span>
        <button disabled={page === totalPages} onClick={() => onPage(page + 1)}
          style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: page === totalPages ? '#f3f4f6' : '#fff', fontSize: '13px', color: page === totalPages ? '#bbb' : '#333' }}>→</button>
      </div>
    </div>
  )
}

function fmt(n: number) { return n.toLocaleString('ru') }
function fmtDate(d: string) { return new Date(d).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: '2-digit' }) }

// ── Stats Tab ──────────────────────────────────────────────────────────────────

function StatsTab() {
  const [stats, setStats] = useState<AdminStats | null>(null)

  useEffect(() => {
    api.get<AdminStats>('/admin/stats').then(setStats).catch(() => null)
  }, [])

  if (!stats) return <div style={{ padding: '48px', textAlign: 'center', color: '#888' }}>Загрузка…</div>

  const conversionRate = stats.totalUsers > 0
    ? ((stats.activeSubscriptions / stats.totalUsers) * 100).toFixed(1)
    : '0.0'

  const kpis = [
    { label: 'Пользователей всего', value: fmt(stats.totalUsers), sub: `+${stats.newUsersThisWeek} за неделю`, color: '#5b6af0' },
    { label: 'Платных подписок', value: fmt(stats.activeSubscriptions), sub: `конверсия ${conversionRate}%`, color: '#10b981' },
    { label: 'Ориент. MRR', value: `${fmt(stats.estimatedMrr)} ₽`, sub: 'по тарифам без скидок', color: '#f59e0b' },
    { label: 'Сайтов', value: fmt(stats.totalSites), sub: `${stats.publishedSites} опубликовано`, color: '#7c3aed' },
    { label: 'Заявок', value: fmt(stats.totalFormSubmissions), sub: 'через формы', color: '#ec4899' },
    { label: 'Заказов', value: fmt(stats.totalOrders), sub: 'через магазины', color: '#14b8a6' },
  ]

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', marginBottom: '28px' }}>
        {kpis.map(k => (
          <div key={k.label} style={{ padding: '20px 24px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', borderLeft: `4px solid ${k.color}` }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{k.label}</div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#111', lineHeight: 1 }}>{k.value}</div>
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px 24px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '16px' }}>Разбивка по тарифам</div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {stats.planBreakdown.sort((a, b) => {
            const order = ['FREE', 'HOSTING', 'PERSONAL', 'BUSINESS', 'AGENCY', 'ENTERPRISE']
            return order.indexOf(a.plan) - order.indexOf(b.plan)
          }).map(row => (
            <div key={row.plan} style={{ padding: '12px 20px', background: '#f8f9ff', border: `1px solid ${PLAN_COLORS[row.plan] ?? '#e5e7eb'}`, borderRadius: '10px', textAlign: 'center', minWidth: '100px' }}>
              <div style={{ fontSize: '24px', fontWeight: 900, color: PLAN_COLORS[row.plan] ?? '#333' }}>{row.count}</div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#666', marginTop: '2px' }}>{row.plan}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Users Tab ──────────────────────────────────────────────────────────────────

function UsersTab() {
  const navigate = useNavigate()
  const [data, setData] = useState<Paginated<AdminUser> | null>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [planLoading, setPlanLoading] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null)
  const [impersonating, setImpersonating] = useState<string | null>(null)

  const load = useCallback(() => {
    api.get<Paginated<AdminUser>>(`/admin/users?page=${page}&limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`)
      .then(setData).catch(() => null)
  }, [page, search])

  useEffect(() => { load() }, [load])

  const changePlan = async (userId: string, plan: string) => {
    setPlanLoading(userId)
    try {
      await api.patch(`/admin/users/${userId}/plan`, { plan })
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setPlanLoading(null)
    }
  }

  const deleteUser = async (user: AdminUser) => {
    try {
      await api.delete(`/admin/users/${user.id}`)
      setDeleteConfirm(null)
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  const impersonate = async (user: AdminUser) => {
    if (!confirm(`Войти как ${user.email}?\n\nВы будете перенаправлены в дашборд под его аккаунтом. Для возврата нажмите «Вернуться в Admin» в баннере.`)) return
    setImpersonating(user.id)
    try {
      const res = await api.post<{ token: string; email: string }>(`/admin/users/${user.id}/impersonate`, {})
      const currentToken = localStorage.getItem('bst-token') ?? ''
      sessionStorage.setItem('bst-admin-token', currentToken)
      sessionStorage.setItem('bst-admin-email', user.email)
      localStorage.setItem('bst-token', res.token)
      localStorage.setItem('bst-auth', 'true')
      navigate('/dashboard')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setImpersonating(null)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1) } }}
          placeholder="Поиск по email или имени..."
          style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px' }}
        />
        <button
          onClick={() => { setSearch(searchInput); setPage(1) }}
          style={{ padding: '10px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}
        >Найти</button>
        {search && (
          <button onClick={() => { setSearch(''); setSearchInput(''); setPage(1) }}
            style={{ padding: '10px 16px', background: '#f3f4f6', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#666' }}>
            Сброс
          </button>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Email', 'Имя', 'Тариф', 'Сайтов', 'Регистрация', 'Действия'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.data.map((user, i) => (
              <tr key={user.id} style={{ borderTop: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                  <div style={{ fontWeight: 600, color: '#111' }}>{user.email}</div>
                  {user.hasActiveSubscription && <div style={{ fontSize: '10px', color: '#10b981', fontWeight: 700 }}>● Подписка активна</div>}
                  {user.isAdmin && <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: 700 }}>● Admin</div>}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#555' }}>{user.name || '—'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <select
                    value={user.plan}
                    disabled={planLoading === user.id}
                    onChange={e => changePlan(user.id, e.target.value)}
                    style={{ padding: '4px 8px', border: `1px solid ${PLAN_COLORS[user.plan] ?? '#e5e7eb'}`, borderRadius: '7px', fontSize: '12px', fontWeight: 700, color: PLAN_COLORS[user.plan] ?? '#333', background: '#fff', cursor: 'pointer' }}
                  >
                    {PLAN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#555', textAlign: 'center' }}>{user.sitesCount}</td>
                <td style={{ padding: '12px 16px', fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>{fmtDate(user.createdAt)}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {!user.isAdmin && (
                      <button
                        onClick={() => impersonate(user)}
                        disabled={impersonating === user.id}
                        style={{ padding: '4px 10px', background: '#f0f4ff', color: '#5b6af0', border: '1px solid #c7d2fe', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >Войти как</button>
                    )}
                    <button
                      onClick={() => setDeleteConfirm(user)}
                      style={{ padding: '4px 10px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                    >Удалить</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.data.length) && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
            {data ? 'Пользователи не найдены' : 'Загрузка…'}
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={data?.totalPages ?? 1} total={data?.total ?? 0} unit="пользователей" onPage={setPage} />

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 32px', maxWidth: '400px', width: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#ef4444', marginBottom: '12px' }}>Удалить пользователя?</div>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#555', lineHeight: 1.6 }}>
              Будут удалены: аккаунт, все сайты, страницы, CMS, заявки и файлы<br />
              <strong>{deleteConfirm.email}</strong>
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f3f4f6', cursor: 'pointer', fontWeight: 600, fontSize: '14px', color: '#555' }}>Отмена</button>
              <button onClick={() => deleteUser(deleteConfirm)}
                style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>Удалить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sites Tab ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  PUBLISHED: '#10b981',
  DRAFT: '#9ca3af',
  ARCHIVED: '#f59e0b',
}

function SitesTab() {
  const [data, setData] = useState<Paginated<AdminSite> | null>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [suspendLoading, setSuspendLoading] = useState<string | null>(null)
  const [suspendDialog, setSuspendDialog] = useState<AdminSite | null>(null)
  const [suspendReason, setSuspendReason] = useState('')

  const load = useCallback(() => {
    api.get<Paginated<AdminSite>>(`/admin/sites?page=${page}&limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`)
      .then(setData).catch(() => null)
  }, [page, search])

  useEffect(() => { load() }, [load])

  const deleteSite = async (id: string) => {
    try {
      await api.delete(`/admin/sites/${id}`)
      setDeleteId(null)
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  const toggleSuspend = async (site: AdminSite) => {
    if (!site.suspended) {
      setSuspendDialog(site)
      setSuspendReason('')
      return
    }
    setSuspendLoading(site.id)
    try {
      await api.patch(`/admin/sites/${site.id}/suspend`, { suspended: false })
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSuspendLoading(null)
    }
  }

  const confirmSuspend = async () => {
    if (!suspendDialog) return
    setSuspendLoading(suspendDialog.id)
    try {
      await api.patch(`/admin/sites/${suspendDialog.id}/suspend`, { suspended: true, reason: suspendReason || undefined })
      setSuspendDialog(null)
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSuspendLoading(null)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1) } }}
          placeholder="Поиск по slug, имени или email владельца..."
          style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px' }} />
        <button onClick={() => { setSearch(searchInput); setPage(1) }}
          style={{ padding: '10px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}>Найти</button>
        {search && <button onClick={() => { setSearch(''); setSearchInput(''); setPage(1) }}
          style={{ padding: '10px 16px', background: '#f3f4f6', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#666' }}>Сброс</button>}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Сайт', 'Статус', 'Владелец', 'Страниц', 'Заявок', 'Дата', 'Действия'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.data.map((site, i) => (
              <tr key={site.id} style={{ borderTop: '1px solid #f0f0f0', background: site.suspended ? '#fff8f8' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#111' }}>{site.name || site.slug}</div>
                  <div style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace' }}>{site.slug}.mybst.ru</div>
                  {site.customDomain && <div style={{ fontSize: '11px', color: '#5b6af0' }}>🌐 {site.customDomain}</div>}
                  {site.suspended && <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: 700 }}>🚫 Заблокирован{site.suspensionReason ? ` — ${site.suspensionReason}` : ''}</div>}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, color: STATUS_COLORS[site.status] ?? '#888', background: `${STATUS_COLORS[site.status] ?? '#888'}18` }}>
                    {site.status}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '12px', color: '#555' }}>
                  <div>{site.ownerEmail}</div>
                  <PlanBadge plan={site.ownerPlan} />
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#555', textAlign: 'center' }}>{site.pagesCount}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#555', textAlign: 'center' }}>{site.submissionsCount}</td>
                <td style={{ padding: '12px 16px', fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>{fmtDate(site.createdAt)}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {site.status === 'PUBLISHED' && !site.suspended && (
                      <a href={`https://${site.slug}.mybst.ru`} target="_blank" rel="noreferrer"
                        style={{ padding: '4px 10px', background: '#f0f1ff', color: '#5b6af0', border: '1px solid #c7d2fe', borderRadius: '7px', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}>
                        ↗
                      </a>
                    )}
                    <button
                      onClick={() => toggleSuspend(site)}
                      disabled={suspendLoading === site.id}
                      style={{
                        padding: '4px 10px',
                        background: site.suspended ? '#f0fdf4' : '#fff8f8',
                        color: site.suspended ? '#16a34a' : '#f59e0b',
                        border: `1px solid ${site.suspended ? '#86efac' : '#fcd34d'}`,
                        borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >{site.suspended ? 'Разблок' : 'Блок'}</button>
                    <button onClick={() => setDeleteId(site.id)}
                      style={{ padding: '4px 10px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.data.length) && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>{data ? 'Сайты не найдены' : 'Загрузка…'}</div>
        )}
      </div>

      <Pagination page={page} totalPages={data?.totalPages ?? 1} total={data?.total ?? 0} unit="сайтов" onPage={setPage} />

      {/* Suspend reason dialog */}
      {suspendDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 32px', maxWidth: '420px', width: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#f59e0b', marginBottom: '8px' }}>Заблокировать сайт?</div>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#555' }}>
              Сайт <strong>{suspendDialog.slug}.mybst.ru</strong> станет недоступен для посетителей.
            </p>
            <input
              value={suspendReason}
              onChange={e => setSuspendReason(e.target.value)}
              placeholder="Причина блокировки (необязательно)"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setSuspendDialog(null)}
                style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f3f4f6', cursor: 'pointer', fontWeight: 600, fontSize: '14px', color: '#555' }}>Отмена</button>
              <button onClick={confirmSuspend}
                style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: '#f59e0b', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>Заблокировать</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 32px', maxWidth: '360px', width: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#ef4444', marginBottom: '12px' }}>Удалить сайт?</div>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#555' }}>Все страницы, блоки и данные будут удалены без возможности восстановления.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setDeleteId(null)} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#f3f4f6', cursor: 'pointer', fontWeight: 600, fontSize: '14px', color: '#555' }}>Отмена</button>
              <button onClick={() => deleteSite(deleteId)} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>Удалить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Waitlist Tab ───────────────────────────────────────────────────────────────

function WaitlistTab() {
  const [data, setData] = useState<Paginated<WaitlistEntry> | null>(null)
  const [page, setPage] = useState(1)

  const load = useCallback(() => {
    api.get<Paginated<WaitlistEntry>>(`/admin/waitlist?page=${page}&limit=50`)
      .then(setData).catch(() => null)
  }, [page])

  useEffect(() => { load() }, [load])

  const remove = async (id: string) => {
    if (!confirm('Удалить из листа ожидания?')) return
    await api.delete(`/admin/waitlist/${id}`).catch(() => null)
    load()
  }

  const exportCsv = () => {
    if (!data?.data.length) return
    const rows = [['Позиция', 'Email', 'Дата'], ...data.data.map(e => [String(e.position), e.email, fmtDate(e.createdAt)])]
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'waitlist.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: '#555' }}>
          Всего в листе ожидания: <strong>{data ? fmt(data.total) : '…'}</strong>
        </div>
        <button onClick={exportCsv}
          style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '9px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
          ⬇ CSV
        </button>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['#', 'Email', 'Дата', ''].map((h, i) => (
                <th key={i} style={{ padding: '12px 16px', textAlign: i === 0 ? 'center' : 'left', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.data.map((entry, i) => (
              <tr key={entry.id} style={{ borderTop: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: '14px', fontWeight: 800, color: '#5b6af0' }}>{entry.position}</td>
                <td style={{ padding: '10px 16px', fontSize: '13px', color: '#111', fontWeight: 500 }}>{entry.email}</td>
                <td style={{ padding: '10px 16px', fontSize: '12px', color: '#888' }}>{fmtDate(entry.createdAt)}</td>
                <td style={{ padding: '10px 16px' }}>
                  <button onClick={() => remove(entry.id)}
                    style={{ padding: '3px 8px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.data.length) && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>{data ? 'Лист ожидания пуст' : 'Загрузка…'}</div>
        )}
      </div>

      {data && data.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: page === 1 ? '#f3f4f6' : '#fff', fontSize: '13px', color: page === 1 ? '#bbb' : '#333' }}>←</button>
          <span style={{ padding: '6px 12px', fontSize: '13px', color: '#555' }}>{page} / {data.totalPages}</span>
          <button disabled={page === data.totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: page === data.totalPages ? '#f3f4f6' : '#fff', fontSize: '13px', color: page === data.totalPages ? '#bbb' : '#333' }}>→</button>
        </div>
      )}
    </div>
  )
}

// ── Broadcast Tab ─────────────────────────────────────────────────────────────

function BroadcastTab() {
  const [subject, setSubject] = useState('')
  const [html, setHtml] = useState('')
  const [filter, setFilter] = useState<'all' | 'paid' | 'free'>('all')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ sent: number; total: number } | null>(null)
  const [preview, setPreview] = useState(false)

  const send = async () => {
    if (!subject.trim() || !html.trim()) { alert('Заполните тему и тело письма'); return }
    const filterLabel = filter === 'all' ? 'всем пользователям' : filter === 'paid' ? 'платным пользователям' : 'бесплатным пользователям'
    if (!confirm(`Отправить письмо «${subject}» ${filterLabel}?\n\nЭто действие нельзя отменить.`)) return
    setLoading(true)
    setResult(null)
    try {
      const res = await api.post<{ sent: number; total: number }>('/admin/broadcast', { subject, html, filter })
      setResult(res)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка отправки')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      <div style={{ background: '#fff8ed', border: '1px solid #fcd34d', borderRadius: '12px', padding: '14px 18px', marginBottom: '24px', fontSize: '13px', color: '#92400e', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '18px' }}>⚠</span>
        <div>
          <strong>Используйте осторожно.</strong> Рассылка уходит сразу всем выбранным пользователям через SMTP. Проверьте содержимое через предпросмотр перед отправкой.
        </div>
      </div>

      <div style={{ display: 'grid', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>Аудитория</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            {(['all', 'paid', 'free'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '8px 18px', borderRadius: '9px', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                  border: filter === f ? '2px solid #5b6af0' : '1px solid #e5e7eb',
                  background: filter === f ? '#f0f1ff' : '#fff',
                  color: filter === f ? '#5b6af0' : '#555',
                }}
              >
                {f === 'all' ? '👥 Все' : f === 'paid' ? '💳 Платные' : '🆓 Бесплатные'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>Тема письма</label>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Тема письма..."
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px' }}
          />
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '13px', fontWeight: 700, color: '#555' }}>HTML-тело письма</label>
            <button
              onClick={() => setPreview(p => !p)}
              style={{ padding: '4px 12px', background: '#f3f4f6', border: 'none', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', color: '#555', fontWeight: 600 }}
            >{preview ? 'Редактировать' : 'Предпросмотр'}</button>
          </div>
          {preview ? (
            <div
              style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', minHeight: '200px', background: '#fff' }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <textarea
              value={html}
              onChange={e => setHtml(e.target.value)}
              placeholder="<!DOCTYPE html>..."
              rows={12}
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', fontFamily: 'monospace', resize: 'vertical' }}
            />
          )}
        </div>

        <button
          onClick={send}
          disabled={loading}
          style={{ padding: '13px 28px', background: loading ? '#9ca3af' : '#5b6af0', color: '#fff', border: 'none', borderRadius: '11px', fontWeight: 800, fontSize: '15px', cursor: loading ? 'default' : 'pointer', width: 'fit-content' }}
        >
          {loading ? '⏳ Отправка…' : '✉ Отправить рассылку'}
        </button>

        {result && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '12px', padding: '16px 20px', fontSize: '14px', color: '#166534', fontWeight: 600 }}>
            ✓ Отправлено {result.sent} из {result.total} писем
          </div>
        )}
      </div>
    </div>
  )
}

// ── Settings Tab ───────────────────────────────────────────────────────────────

const SETTING_PRESETS = [
  { key: 'feature_ai_enabled', label: 'AI-генерация сайта', description: 'Включить/выключить AI для всех пользователей', type: 'bool' },
  { key: 'feature_showcase_enabled', label: 'Витрина сайтов', description: 'Показывать публичную витрину', type: 'bool' },
  { key: 'registration_open', label: 'Открыта регистрация', description: 'Разрешить новым пользователям регистрироваться', type: 'bool' },
  { key: 'waitlist_enabled', label: 'Лист ожидания активен', description: 'Вместо регистрации — добавление в лист ожидания', type: 'bool' },
  { key: 'maintenance_message', label: 'Сообщение о тех. работах', description: 'Если задано — показывается на главной странице', type: 'text' },
  { key: 'banner_message', label: 'Глобальный баннер', description: 'Текст баннера в верхней части дашборда (пусто — баннер скрыт)', type: 'text' },
]

function SettingsTab() {
  const [settings, setSettings] = useState<AppSetting[]>([])
  const [editing, setEditing] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')

  const load = useCallback(() => {
    api.get<AppSetting[]>('/admin/settings').then(setSettings).catch(() => null)
  }, [])

  useEffect(() => { load() }, [load])

  const getVal = (key: string) => {
    if (editing[key] !== undefined) return editing[key]
    return settings.find(s => s.key === key)?.value ?? ''
  }

  const save = async (key: string) => {
    const value = editing[key] ?? settings.find(s => s.key === key)?.value ?? ''
    setSaving(key)
    try {
      await api.patch(`/admin/settings/${encodeURIComponent(key)}`, { value })
      setEditing(e => { const next = { ...e }; delete next[key]; return next })
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(null)
    }
  }

  const addCustom = async () => {
    if (!newKey.trim()) { alert('Введите ключ'); return }
    setSaving('__new')
    try {
      await api.patch(`/admin/settings/${encodeURIComponent(newKey.trim())}`, { value: newValue })
      setNewKey('')
      setNewValue('')
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(null)
    }
  }

  const presetKeys = new Set(SETTING_PRESETS.map(p => p.key))
  const customSettings = settings.filter(s => !presetKeys.has(s.key))

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', marginBottom: '24px' }}>
        <div style={{ padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', fontSize: '13px', fontWeight: 700, color: '#333' }}>
          Системные настройки
        </div>
        <div>
          {SETTING_PRESETS.map((preset, i) => {
            const val = getVal(preset.key)
            const isDirty = editing[preset.key] !== undefined
            return (
              <div key={preset.key} style={{ padding: '16px 20px', borderTop: i === 0 ? 'none' : '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '2px' }}>{preset.label}</div>
                  <div style={{ fontSize: '12px', color: '#888', fontFamily: 'monospace' }}>{preset.key}</div>
                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{preset.description}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  {preset.type === 'bool' ? (
                    <button
                      onClick={() => {
                        const next = val === 'true' ? 'false' : 'true'
                        setEditing(e => ({ ...e, [preset.key]: next }))
                      }}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                        background: val === 'true' ? '#5b6af0' : '#d1d5db', position: 'relative', transition: 'background 0.2s',
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: '2px', left: val === 'true' ? '22px' : '2px',
                        width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                  ) : (
                    <input
                      value={val}
                      onChange={e => setEditing(ed => ({ ...ed, [preset.key]: e.target.value }))}
                      style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', width: '180px' }}
                    />
                  )}
                  {isDirty && (
                    <button
                      onClick={() => save(preset.key)}
                      disabled={saving === preset.key}
                      style={{ padding: '5px 12px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                    >{saving === preset.key ? '…' : 'Сохр.'}</button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Custom settings */}
      {customSettings.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', marginBottom: '24px' }}>
          <div style={{ padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', fontSize: '13px', fontWeight: 700, color: '#333' }}>
            Пользовательские ключи
          </div>
          {customSettings.map((s, i) => (
            <div key={s.key} style={{ padding: '12px 20px', borderTop: i === 0 ? 'none' : '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#555', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.key}</div>
              <input
                value={getVal(s.key)}
                onChange={e => setEditing(ed => ({ ...ed, [s.key]: e.target.value }))}
                style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', width: '200px' }}
              />
              {editing[s.key] !== undefined && (
                <button onClick={() => save(s.key)} disabled={saving === s.key}
                  style={{ padding: '5px 12px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                  {saving === s.key ? '…' : 'Сохр.'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new setting */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#333', marginBottom: '14px' }}>Добавить ключ</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            placeholder="feature_key"
            style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '9px', fontSize: '13px', fontFamily: 'monospace' }}
          />
          <input
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
            placeholder="Значение"
            style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '9px', fontSize: '13px' }}
          />
          <button onClick={addCustom} disabled={saving === '__new'}
            style={{ padding: '9px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {saving === '__new' ? '…' : '+ Добавить'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Monitoring Tab ────────────────────────────────────────────────────────────

interface MonitoringData {
  status: 'ok' | 'degraded'
  uptime: string
  uptimeMs: number
  environment: string
  version: string
  timestamp: string
  checks: Record<string, { status: 'ok' | 'error' | 'skip'; latencyMs?: number; error?: string }>
  requests: { total: number; errors: number; errorRate: string }
  memory: { heapUsedMb: number; heapTotalMb: number; rssMb: number }
  topRoutes: Array<{ route: string; count: number; errors: number; avgMs: number; maxMs: number }>
}

const CHECK_COLORS: Record<string, string> = {
  ok: '#10b981',
  error: '#ef4444',
  skip: '#9ca3af',
}

const CHECK_LABELS: Record<string, string> = {
  ok: '● OK',
  error: '● ОШИБКА',
  skip: '○ ПРОПУЩЕН',
}

const CHECK_NAMES: Record<string, string> = {
  database: 'PostgreSQL',
  redis: 'Redis',
  storage: 'Хранилище',
}

function MonitoringTab() {
  const [data, setData] = useState<MonitoringData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
  const [autoRefresh, setAutoRefresh] = useState(true)

  const load = useCallback(() => {
    api.get<MonitoringData>('/admin/monitoring')
      .then(d => { setData(d); setError(null); setLastRefresh(new Date()) })
      .catch(e => setError(e instanceof Error ? e.message : 'Ошибка загрузки'))
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(load, 30_000)
    return () => clearInterval(interval)
  }, [load, autoRefresh])

  const heapPct = data ? Math.round((data.memory.heapUsedMb / data.memory.heapTotalMb) * 100) : 0
  const heapColor = heapPct > 85 ? '#ef4444' : heapPct > 65 ? '#f59e0b' : '#10b981'

  return (
    <div style={{ maxWidth: '900px' }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%',
            background: data?.status === 'ok' ? '#10b981' : data?.status === 'degraded' ? '#f59e0b' : '#9ca3af',
            boxShadow: data?.status === 'ok' ? '0 0 6px #10b981' : 'none',
          }} />
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#111' }}>
            {data?.status === 'ok' ? 'Все системы в норме' : data?.status === 'degraded' ? 'Деградация' : 'Загрузка…'}
          </span>
        </div>
        <span style={{ fontSize: '12px', color: '#888', marginLeft: 'auto' }}>
          Обновлено: {lastRefresh.toLocaleTimeString('ru')}
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', cursor: 'pointer' }}>
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          Авто (30с)
        </label>
        <button onClick={load} style={{ padding: '5px 12px', background: '#f3f4f6', border: 'none', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', color: '#555', fontWeight: 600 }}>
          ↻ Обновить
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#ef4444' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {/* Health checks */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' }}>Состояние сервисов</div>
          {data ? Object.entries(data.checks).map(([key, check]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>{CHECK_NAMES[key] ?? key}</span>
                {check.error && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>{check.error.slice(0, 60)}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: CHECK_COLORS[check.status] }}>
                  {CHECK_LABELS[check.status]}
                </span>
                {check.latencyMs !== undefined && (
                  <div style={{ fontSize: '11px', color: '#888' }}>{check.latencyMs}мс</div>
                )}
              </div>
            </div>
          )) : <div style={{ color: '#aaa', fontSize: '13px' }}>Загрузка…</div>}
          {data && (
            <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <span style={{ color: '#888' }}>Uptime: </span>
                <span style={{ fontWeight: 700, color: '#111' }}>{data.uptime}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <span style={{ color: '#888' }}>Среда: </span>
                <span style={{ fontWeight: 700, color: '#5b6af0' }}>{data.environment}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <span style={{ color: '#888' }}>Версия: </span>
                <span style={{ fontWeight: 700 }}>{data.version}</span>
              </div>
            </div>
          )}
        </div>

        {/* Memory + Request metrics */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Memory */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Память (Heap)</div>
            {data ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '22px', fontWeight: 900, color: '#111' }}>{data.memory.heapUsedMb} MB</span>
                  <span style={{ fontSize: '13px', color: '#888', alignSelf: 'flex-end' }}>/ {data.memory.heapTotalMb} MB</span>
                </div>
                <div style={{ background: '#f3f4f6', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                  <div style={{ background: heapColor, height: '100%', width: `${Math.min(heapPct, 100)}%`, transition: 'width 0.5s', borderRadius: '6px' }} />
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>RSS: {data.memory.rssMb} MB · {heapPct}% использовано</div>
              </>
            ) : <div style={{ color: '#aaa', fontSize: '13px' }}>Загрузка…</div>}
          </div>

          {/* Requests */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>HTTP запросы (с запуска)</div>
            {data ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {[
                  { label: 'Всего', value: fmt(data.requests.total), color: '#5b6af0' },
                  { label: 'Ошибок', value: fmt(data.requests.errors), color: '#ef4444' },
                  { label: 'Error rate', value: data.requests.errorRate, color: data.requests.errors > 0 ? '#f59e0b' : '#10b981' },
                ].map(item => (
                  <div key={item.label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 900, color: item.color }}>{item.value}</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{item.label}</div>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: '#aaa', fontSize: '13px' }}>Загрузка…</div>}
          </div>
        </div>
      </div>

      {/* Top routes table */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>Топ маршрутов по запросам</div>
          {data && (
            <a href={`${window.location.origin.replace('3001', '3000')}/api/metrics`} target="_blank" rel="noreferrer"
              style={{ fontSize: '12px', color: '#5b6af0', textDecoration: 'none', fontWeight: 600 }}>
              ↗ Prometheus /metrics
            </a>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Маршрут', 'Запросов', 'Ошибок', 'Avg мс', 'Max мс'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Маршрут' ? 'left' : 'right', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.topRoutes.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '9px 16px', fontFamily: 'monospace', fontSize: '12px', color: '#333' }}>{r.route}</td>
                <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#111' }}>{fmt(r.count)}</td>
                <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: '13px', color: r.errors > 0 ? '#ef4444' : '#10b981', fontWeight: r.errors > 0 ? 700 : 400 }}>
                  {r.errors > 0 ? fmt(r.errors) : '—'}
                </td>
                <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: '13px', color: r.avgMs > 500 ? '#f59e0b' : '#555' }}>{r.avgMs} мс</td>
                <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: '13px', color: r.maxMs > 2000 ? '#ef4444' : '#888' }}>{r.maxMs} мс</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.topRoutes.length) && (
          <div style={{ padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
            {data ? 'Нет данных — запросы ещё не поступали' : 'Загрузка…'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── LaunchTab ─────────────────────────────────────────────────────────────────

interface LaunchStatus {
  emailSent: boolean; sentAt: string | null; userCount: number; waitlistCount: number; totalAudience: number
}

const TG_DEFAULT_TEXT = `🚀 Запускаю BST — конструктор сайтов с AI

Описываешь бизнес → AI создаёт сайт за 30 секунд → публикуешь.

4 дизайн-стиля: Noir / Atelier / Roast / Volt
• 376+ блоков drag & drop
• Магазин с ЮКасса (T-Pay, SberPay, СБП)
• Lighthouse 90+ из коробки
• Бесплатно

→ mybst.ru

Буду рад фидбэку 🙏`

function LaunchTab() {
  const [status, setStatus] = useState<LaunchStatus | null>(null)
  const [phUrl, setPhUrl] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<{ sent: number; skipped: number; already: boolean } | null>(null)
  const [tgChatId, setTgChatId] = useState('')
  const [tgText, setTgText] = useState(TG_DEFAULT_TEXT)
  const [tgSending, setTgSending] = useState(false)
  const [tgResult, setTgResult] = useState<string | null>(null)

  const CHECKLIST = [
    { done: true,  label: 'Landing page готова — AI-first narrative, 4 bento-стиля' },
    { done: true,  label: 'sitemap.xml и robots.txt созданы' },
    { done: true,  label: 'CRM bridge — формы и заказы → Bitrix24/AmoCRM/SMS/GetCourse' },
    { done: true,  label: 'Resend email transport настроен' },
    { done: true,  label: 'S3/MinIO — медиафайлы в продакшне' },
    { done: true,  label: 'VPS куплен, Nginx + PM2 + SSL настроены — mybst.ru LIVE' },
    { done: true,  label: 'Все 3 сервиса запущены: API / Editor / Renderer' },
    { done: false, label: 'GA4 измерение ID настроено (VITE_GA_ID)' },
    { done: false, label: 'ProductHunt листинг опубликован (см. launch/producthunt.md)' },
    { done: false, label: 'Пост на VC.ru / Habr (см. launch/vcru-article.md)' },
    { done: false, label: 'Посты в Telegram-каналах (см. launch/telegram.md)' },
    { done: false, label: 'Launch email отправлен (кнопка ниже)' },
  ]

  useEffect(() => {
    api.get<LaunchStatus>('/admin/launch/status').then(setStatus).catch(() => null)
  }, [])

  const handleSend = async () => {
    if (!confirm(`Отправить launch email ${status?.totalAudience ?? '?'} получателям? Это можно сделать только один раз.`)) return
    setSending(true)
    try {
      const result = await api.post<{ sent: number; skipped: number; already: boolean }>('/admin/launch/email', { phUrl: phUrl || undefined })
      setSendResult(result)
      const updated = await api.get<LaunchStatus>('/admin/launch/status')
      setStatus(updated)
    } catch (e) {
      alert('Ошибка: ' + String(e))
    } finally {
      setSending(false)
    }
  }

  const handleTgSend = async () => {
    if (!tgChatId.trim()) { alert('Укажи ID или @username канала'); return }
    if (!tgText.trim()) return
    setTgSending(true)
    setTgResult(null)
    try {
      const res = await api.post<{ ok: boolean; description?: string }>('/admin/launch/telegram', { chatId: tgChatId.trim(), text: tgText.trim() })
      setTgResult(res.ok ? '✅ Отправлено!' : `❌ Ошибка: ${res.description ?? 'неизвестная'}`)
    } catch (e) {
      setTgResult('❌ ' + String(e))
    } finally {
      setTgSending(false)
    }
  }

  const s = { card: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '16px' } as React.CSSProperties }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
      {/* Left column */}
      <div>
        {/* Audience */}
        <div style={s.card}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>📬 Аудитория для запуска</h3>
          {status ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: 'Пользователей', value: status.userCount },
                { label: 'В листе ожидания', value: status.waitlistCount },
                { label: 'Всего получателей', value: status.totalAudience },
              ].map(item => (
                <div key={item.label} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#111' }}>{item.value}</div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{item.label}</div>
                </div>
              ))}
            </div>
          ) : <div style={{ color: '#888', fontSize: '13px' }}>Загрузка…</div>}

          {/* Status */}
          {status?.emailSent ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#15803d' }}>
              ✅ Launch email отправлен {status.sentAt ? `· ${new Date(status.sentAt).toLocaleString('ru')}` : ''}
            </div>
          ) : (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#c2410c' }}>
              ⏳ Email ещё не отправлен
            </div>
          )}
        </div>

        {/* Send form */}
        <div style={s.card}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>🚀 Отправить launch email</h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>
              Product Hunt URL (опционально)
            </label>
            <input
              value={phUrl}
              onChange={e => setPhUrl(e.target.value)}
              placeholder="https://www.producthunt.com/posts/bst"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' as const }}
            />
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#888' }}>Если заполнено — в письмо добавится кнопка "Проголосовать на PH"</p>
          </div>
          <button
            onClick={handleSend}
            disabled={sending || status?.emailSent}
            style={{
              background: status?.emailSent ? '#e5e7eb' : '#111', color: status?.emailSent ? '#888' : '#fff',
              border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 700, fontSize: '14px',
              cursor: status?.emailSent ? 'not-allowed' : 'pointer', width: '100%',
            }}>
            {sending ? '⏳ Отправка…' : status?.emailSent ? '✅ Уже отправлено' : '📢 Отправить всем сейчас'}
          </button>
          {sendResult && !sendResult.already && (
            <div style={{ marginTop: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#15803d' }}>
              ✅ Отправлено: {sendResult.sent} · Пропущено: {sendResult.skipped}
            </div>
          )}
        </div>

        {/* PH Badge */}
        <div style={s.card}>
          <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700 }}>🏅 Product Hunt Badge</h3>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>
            После публикации на PH скопируй код бейджа и вставь в LandingPage.tsx:
          </p>
          <pre style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', fontSize: '11px', overflowX: 'auto', margin: 0, color: '#333' }}>
{`<a href="https://www.producthunt.com/posts/bst" target="_blank">
  <img
    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=YOUR_ID&theme=dark"
    alt="BST - Product Hunt"
    style={{width:'250px',height:'54px'}}
  />
</a>`}
          </pre>
        </div>

        {/* Telegram Post */}
        <div style={s.card}>
          <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700 }}>📢 Пост в Telegram</h3>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>
            Бот должен быть администратором канала. Используй @username или числовой chat_id.
          </p>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '5px' }}>Канал / Chat ID</label>
            <input
              value={tgChatId}
              onChange={e => setTgChatId(e.target.value)}
              placeholder="@bst_app или -1001234567890"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' as const }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '5px' }}>Текст поста</label>
            <textarea
              value={tgText}
              onChange={e => setTgText(e.target.value)}
              rows={10}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box' as const, resize: 'vertical' }}
            />
          </div>
          <button
            onClick={handleTgSend}
            disabled={tgSending}
            style={{ background: '#2b7fcc', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 700, fontSize: '14px', cursor: tgSending ? 'wait' : 'pointer', width: '100%' }}>
            {tgSending ? '⏳ Отправка…' : '📤 Отправить в Telegram'}
          </button>
          {tgResult && (
            <div style={{ marginTop: '10px', padding: '10px 14px', background: tgResult.startsWith('✅') ? '#f0fdf4' : '#fef2f2', border: `1px solid ${tgResult.startsWith('✅') ? '#bbf7d0' : '#fecaca'}`, borderRadius: '8px', fontSize: '13px', color: tgResult.startsWith('✅') ? '#15803d' : '#dc2626' }}>
              {tgResult}
            </div>
          )}
        </div>
      </div>

      {/* Right column — checklist */}
      <div>
        <div style={s.card}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>✅ Чеклист запуска</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {CHECKLIST.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 12px', background: item.done ? '#f0fdf4' : '#f8f9fa', borderRadius: '8px', border: `1px solid ${item.done ? '#bbf7d0' : '#e5e7eb'}` }}>
                <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{item.done ? '✅' : '⬜'}</span>
                <span style={{ fontSize: '13px', color: item.done ? '#15803d' : '#374151', lineHeight: 1.4 }}>{item.label}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '20px', padding: '16px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#92400e' }}>📋 Launch channels</p>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#78350f', lineHeight: 1.8 }}>
              <li><b>Product Hunt</b> — запускай в 01:00 МСК (12:00 PST)</li>
              <li><b>VC.ru</b> — статья "Как мы сделали конструктор сайтов"</li>
              <li><b>Habr</b> — технический пост о стеке (NestJS+Next.js)</li>
              <li><b>Telegram</b> — @indie_startup_ru, @designersru, @webdevru</li>
              <li><b>Reddit</b> — r/webdev, r/SaaS, r/indiegaming</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── AI Traces Tab (§99 Observability) ─────────────────────────────────────────

interface AiTrace {
  id: string
  taskType: string
  prompt: string
  result: string | null
  durationMs: number | null
  model: string | null
  success: boolean
  createdAt: string
  userId: string | null
  errors: string[]
}

function AiTracesTab() {
  const [traces, setTraces] = useState<AiTrace[]>([])
  const [stats, setStats] = useState<{ total: number; failed: number; successRate: number; byType: Array<{ taskType: string; count: number; avgDurationMs: number }> } | null>(null)
  const [loading, setLoading] = useState(true)
  const [taskType, setTaskType] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get<AiTrace[]>(`/admin/ai-traces?limit=50${taskType ? `&taskType=${encodeURIComponent(taskType)}` : ''}`),
      api.get<typeof stats>('/admin/ai-traces/stats'),
    ]).then(([t, s]) => { setTraces(t); setStats(s) }).catch(() => {}).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [taskType])

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Всего трейсов', value: stats.total },
            { label: 'Ошибок', value: stats.failed, red: stats.failed > 0 },
            { label: 'Успешность', value: `${stats.successRate}%` },
          ].map(s => (
            <div key={s.label} style={{ background: '#111', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.red ? '#ef4444' : '#fff' }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          value={taskType}
          onChange={e => setTaskType(e.target.value)}
          placeholder="Фильтр по taskType..."
          style={{ padding: '7px 12px', background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12, color: '#fff', fontFamily: 'monospace', width: 220 }}
        />
        <button onClick={load} style={{ padding: '7px 14px', background: '#333', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
          Обновить
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Загрузка...</div>
      ) : traces.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Трейсов нет</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {traces.map(t => (
            <div key={t.id} style={{ background: '#111', borderRadius: 10, padding: '12px 14px', border: `1px solid ${t.success ? '#1a1a2e' : '#3d1515'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: t.success ? '#0d2b0d' : '#3d1515', color: t.success ? '#4ade80' : '#ef4444' }}>
                  {t.success ? 'OK' : 'ERR'}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', fontFamily: 'monospace' }}>{t.taskType}</span>
                {t.model && <span style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>{t.model}</span>}
                {t.durationMs && <span style={{ fontSize: 10, color: '#666' }}>{t.durationMs}ms</span>}
                <span style={{ fontSize: 10, color: '#444', marginLeft: 'auto' }}>{new Date(t.createdAt).toLocaleString('ru')}</span>
              </div>
              <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', marginBottom: t.result ? 4 : 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {t.prompt.slice(0, 200)}{t.prompt.length > 200 ? '...' : ''}
              </div>
              {t.result && (
                <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', borderTop: '1px solid #222', paddingTop: 4 }}>
                  → {t.result.slice(0, 150)}{t.result.length > 150 ? '...' : ''}
                </div>
              )}
              {t.errors?.length > 0 && (
                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>⚠ {t.errors.join('; ')}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Benchmark Tab (§82-84 AI Evaluation) ──────────────────────────────────────

interface BenchmarkCase {
  id: string
  name: string
  category: string
  prompt: string
  minBlocks: number
}

interface BenchmarkResult {
  caseId: string
  caseName: string
  category: string
  success: boolean
  durationMs: number
  blocksGenerated: number
  scorePercent: number
  errors: string[]
  runAt: string
}

const SCORE_COLOR = (n: number) => n >= 80 ? '#4ade80' : n >= 50 ? '#fbbf24' : '#ef4444'

function BenchmarkTab() {
  const [cases, setCases] = useState<BenchmarkCase[]>([])
  const [history, setHistory] = useState<BenchmarkResult[]>([])
  const [running, setRunning] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.get<BenchmarkCase[]>('/admin/benchmark/cases'),
      api.get<BenchmarkResult[]>('/admin/benchmark/history'),
    ]).then(([c, h]) => { setCases(c); setHistory(h) }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  async function runCase(caseId: string) {
    setRunning(caseId)
    try {
      const result = await api.post<BenchmarkResult>(`/admin/benchmark/run/${caseId}`)
      setHistory(prev => [result, ...prev])
    } catch (err) {
      alert('Ошибка: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setRunning(null)
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Загрузка...</div>

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', color: '#fff', fontSize: 18 }}>Тестовые сценарии</h2>
        <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Запустите тест — AI сгенерирует сайт по промпту и система оценит качество</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 32 }}>
        {cases.map(c => (
          <div key={c.id} style={{ background: '#111', borderRadius: 10, padding: '14px 16px', border: '1px solid #222' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>{c.name}</span>
              <span style={{ fontSize: 10, padding: '2px 6px', background: '#1a1a2e', borderRadius: 6, color: '#666' }}>{c.category}</span>
            </div>
            <div style={{ fontSize: 11, color: '#555', marginBottom: 10, lineHeight: 1.4 }}>{c.prompt}</div>
            <button
              onClick={() => void runCase(c.id)}
              disabled={running === c.id}
              style={{
                padding: '6px 14px', background: running === c.id ? '#333' : '#5b6af0',
                color: '#fff', border: 'none', borderRadius: 7, fontSize: 11, fontWeight: 700,
                cursor: running === c.id ? 'not-allowed' : 'pointer',
              }}
            >
              {running === c.id ? '⟳ Запуск...' : '▶ Запустить'}
            </button>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <div>
          <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: 15 }}>История запусков</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.slice(0, 20).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#111', borderRadius: 8, border: `1px solid ${r.success ? '#1a2e1a' : '#2e1a1a'}` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: SCORE_COLOR(r.scorePercent), width: 44, textAlign: 'right', flexShrink: 0 }}>
                  {r.scorePercent}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#cdd6f4' }}>{r.caseName || r.caseId}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>{r.blocksGenerated} блоков · {r.durationMs}ms</div>
                  {r.errors?.length > 0 && <div style={{ fontSize: 11, color: '#ef4444' }}>{r.errors[0]}</div>}
                </div>
                <div style={{ fontSize: 10, color: '#444' }}>{new Date(r.runAt).toLocaleString('ru')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main AdminPage ─────────────────────────────────────────────────────────────

type Tab = 'stats' | 'users' | 'sites' | 'waitlist' | 'broadcast' | 'settings' | 'monitoring' | 'launch' | 'ai-traces' | 'benchmark'

const TABS: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'stats', label: 'Обзор', icon: '📊' },
  { id: 'users', label: 'Пользователи', icon: '👤' },
  { id: 'sites', label: 'Сайты', icon: '🌐' },
  { id: 'waitlist', label: 'Лист ожидания', icon: '⏳' },
  { id: 'broadcast', label: 'Рассылка', icon: '✉' },
  { id: 'settings', label: 'Настройки', icon: '⚙' },
  { id: 'monitoring', label: 'Мониторинг', icon: '❤' },
  { id: 'launch', label: 'Запуск', icon: '🚀' },
  { id: 'ai-traces', label: 'AI Traces', icon: '🧠' },
  { id: 'benchmark', label: 'Benchmark', icon: '🏆' },
]

const TAB_DESC: Record<Tab, string> = {
  stats: 'Общие показатели платформы BST',
  users: 'Управление пользователями, тарифами и имитация входа',
  sites: 'Все сайты на платформе, блокировка и удаление',
  waitlist: 'Список ожидания доступа',
  broadcast: 'Email-рассылка по всей пользовательской базе',
  settings: 'Фиче-флаги и системные параметры платформы',
  monitoring: 'Состояние сервисов, метрики HTTP и использование памяти',
  launch: 'Управление публичным запуском — email-анонс, Product Hunt, чеклист',
  'ai-traces': 'AI Observability — трейсы выполнения задач (§99)',
  benchmark: 'AI Evaluation Framework — §82-84 бенчмарки генерации',
}

export function AdminPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('stats')
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    api.get<{ isAdmin?: boolean }>('/users/me')
      .then(user => {
        if (!user.isAdmin) navigate('/dashboard', { replace: true })
        else setChecking(false)
      })
      .catch(() => navigate('/login', { replace: true }))
  }, [navigate])

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
        <div style={{ fontSize: '14px', color: '#888' }}>Проверка прав доступа…</div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f5', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#111', padding: '0 32px', display: 'flex', alignItems: 'center', height: '56px', gap: '20px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 0 rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => navigate('/dashboard')}
            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '16px', padding: '4px', lineHeight: 1 }}>←</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '24px', height: '24px', background: '#ef4444', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 900 }}>A</div>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: '15px' }}>BST Admin</span>
            <span style={{ background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: 800, padding: '1px 6px', borderRadius: '6px', letterSpacing: '0.05em' }}>PANEL</span>
          </div>
        </div>

        <nav style={{ display: 'flex', gap: '4px', marginLeft: '20px', overflowX: 'auto' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '6px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                background: activeTab === tab.id ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#888',
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>

        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <span style={{ color: '#ef4444', fontWeight: 700 }}>⚠ Панель администратора</span>
        </div>
      </header>

      {/* Content */}
      <main style={{ padding: '28px 32px', maxWidth: '1280px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 800, color: '#111' }}>
            {TABS.find(t => t.id === activeTab)?.icon} {TABS.find(t => t.id === activeTab)?.label}
          </h1>
          <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>{TAB_DESC[activeTab]}</p>
        </div>

        {activeTab === 'stats' && <StatsTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'sites' && <SitesTab />}
        {activeTab === 'waitlist' && <WaitlistTab />}
        {activeTab === 'broadcast' && <BroadcastTab />}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'monitoring' && <MonitoringTab />}
        {activeTab === 'launch' && <LaunchTab />}
        {activeTab === 'ai-traces' && <AiTracesTab />}
        {activeTab === 'benchmark' && <BenchmarkTab />}
      </main>
    </div>
  )
}
